import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Row, Col, Form, Card } from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { IoCloseOutline } from 'react-icons/io5';
import { toast } from 'react-toastify';
//api
import { getRecommendedBooks, saveRecommendBookstores } from 'api/libros';
//components
import Title from 'components/backoffice/texts/title';
import Button from 'components/backoffice/buttonCustom';
import SearchProduct from 'components/backoffice/customInput/SearchProduct';
import SpinnerComponent from 'components/backoffice/SpinnerComponent';
import PaginationLoader from 'components/loader/PaginationLoader';
import DefaultImage from 'assets/img/preview.jpg';

const RecommendBookstores = () => {
  const { t } = useTranslation(['backoffice']);
  const { setTitle } = useOutletContext();
  const [fields, setFields] = useState([]);
  const [initialFields, setInitialFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const hasValidId = (item) => item && (typeof item.id === 'number' || typeof item.id === 'string');

  const fetchRecommendBookstores = async () => {
    try {
      setLoadingData(true);
      const response = await getRecommendedBooks();
      let items = [];
      if (Array.isArray(response)) {
        items = response;
      } else if (Array.isArray(response?.results)) {
        items = response.results;
      } else if (Array.isArray(response?.data?.results)) {
        items = response.data.results;
      } else if (Array.isArray(response?.data)) {
        items = response.data;
      } else {
        console.warn('No se encontró resultados');
      }
      const clean = items.filter(hasValidId);
      const normalized = clean.map((item) => ({
        id: item.id,
        title: item.title ?? item.titulo ?? '',
        image: item.image ?? item.caratula ?? null,
        publicada: item.publicada ?? false,
        raw: item,
      }));
      setFields(normalized);
      setInitialFields(normalized);
      if (!normalized.length) {
        toast.info('No se encontró resultados.');
      }
    } catch (error) {
      toast.error('Error al cargar productos');
    } finally {
      setLoadingData(false);
    }
  };

  const hasChanges = () => {
    if (fields.length !== initialFields.length) return true;
    const fieldIds = fields.map((f) => f.id).sort();
    const initialIds = initialFields.map((f) => f.id).sort();
    return fieldIds.some((id, index) => id !== initialIds[index]);
  };

  useEffect(() => {
    setTitle(t('recommendBookstores'));
    fetchRecommendBookstores();
    return () => {
      setTitle('');
    };
  }, []);

  const onAddProduct = (product) => {
    if (!hasValidId(product)) {
      return;
    }
    setFields((prev) => {
      const exists = prev.some((p) => p && p.id?.toString() === product.id.toString());
      if (exists) {
        toast.success(t('theProductAdded'));
        return prev;
      }
      return [...prev, product];
    });
  };

  const remove = (index) => {
    setFields((prev) => prev.filter((_, i) => i !== index));
  };

  const handleOnDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(fields);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setFields(items);
  };

  const onSubmitForm = async () => {
    try {
      setLoading(true);
      const currentIds = new Set(fields.filter(hasValidId).map((item) => item.id?.toString()));
      const initialIds = new Set(initialFields.filter(hasValidId).map((item) => item.id?.toString()));
      const added = Array.from(currentIds).filter((id) => !initialIds.has(id));
      const removed = Array.from(initialIds).filter((id) => !currentIds.has(id));
      const items = [
        ...added.map((id) => ({ id, recommended: true })),
        ...removed.map((id) => ({ id, recommended: false })),
      ];
      if (items.length === 0) {
        toast.info(t('No hay cambios para guardar'));
        return;
      }
      const payload = { items };
      const response = await saveRecommendBookstores(payload);
      if (response?.ok || response?.status === 200) {
        toast.success(t('creadocorrectamente'));
        setInitialFields(fields);
      } else {
        toast.error(t('mensajeerror'));
      }
    } catch (error) {
      toast.error(t('mensajeerror'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="d-lg-grid">
      <Title text={t('recommendBookstores')} />
      {loadingData ? (
        <PaginationLoader visibility={loadingData} />
      ) : (
        <Row className="mt-4">
          <Col sm={12} md={12} lg={12} className="mb-md-2 mb-lg-0">
            <Row>
              <Col sm={12} md={12} lg={12}>
                <Form.Label className="text-light">{t('seleccionarproducto')}</Form.Label>
                <SearchProduct
                  filterBy="search"
                  onSelected={onAddProduct}
                  labelShow="ean"
                  placeholder={t('searchByEanTitle')}
                />
              </Col>
              <Col sm={12} md={12} lg={12}>
                {fields.length > 0 && (
                  <div className="mt-4 text-muted">
                    <strong className="text-light">{t('listNotVisible')}</strong>
                  </div>
                )}
              </Col>
              <DragDropContext onDragEnd={handleOnDragEnd}>
                <Droppable droppableId="droppable" direction="vertical">
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef} className="mt-4 container-recommend">
                      {fields.map((list, index) =>
                        !hasValidId(list) ? null : (
                          <Draggable key={list.id} draggableId={list.id.toString()} index={index}>
                            {(provided, snapshot) => (
                              <Card
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className={`mt-3 ${snapshot.isDragging ? 'dragging-card' : ''}`}
                              >
                                <Card.Body
                                  className={`d-flex ${
                                    !list.publicada
                                      ? 'bg-custom-gray'
                                      : ''
                                  }`}
                                >
                                  <div className="d-flex col-6">
                                    <img
                                      src={list.image || list.raw?.image || list.raw?.caratula || DefaultImage}
                                      className="recommend-img"
                                      alt={list.title ?? list.titulo ?? 'Sin título'}
                                    />
                                    <h5 className="mx-3">
                                      {list.title ?? list.titulo ?? 'Sin título'}
                                      <div className="small text-muted mt-1">
                                        {list.publicada
                                          ? t('publicado')
                                          : t('noPublicado')}
                                      </div>
                                    </h5>
                                  </div>
                                  <div className="col-6 d-flex justify-content-end">
                                    <IoCloseOutline size="30" onClick={() => remove(index)} />
                                  </div>
                                </Card.Body>
                              </Card>
                            )}
                          </Draggable>
                        ),
                      )}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
              <div className="my-4 d-flex justify-content-end gap-3">
                <Button color="primary" onClick={onSubmitForm} loading={loading} disabled={!hasChanges()}>
                  {t('save')}
                  <SpinnerComponent loading={loading} />
                </Button>
              </div>
            </Row>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default RecommendBookstores;
